import { useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ErrorAlert,
  PasswordField,
  Select,
  SelectItem,
  TextField,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { CargoProvider } from '@breezeai-frontend/cargo-ui';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';

import breezeLogoSrc from '../../assets/breeze_logo.png';
import vesselIllustrationSrc from '../../assets/vessel_illustration.png';
import { MOBILE_MAX_WIDTH } from '../constants';
import {
  type ActivateFormData,
  useActivateUser,
  useGetUserEmailByToken,
} from './useActivation';

const ROLE_OPTIONS = [
  { key: 'owner/c-suite/director', label: 'Owner/C-suite/Director' },
  { key: 'manager/supervisor', label: 'Manager/Supervisor' },
  { key: 'agent/operative', label: 'Agent/Operative' },
  { key: 'other', label: 'Other' },
];

const DEPARTMENT_OPTIONS = [
  { key: 'freight operations', label: 'Freight Operations' },
  { key: 'insurance/risk management', label: 'Insurance/Risk Management' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'other', label: 'Other' },
];

const ActivateHeader = () => (
  <>
    <div className="grid items-center justify-center h-fit">
      <img src={breezeLogoSrc} alt="Breeze" className="h-8" />
    </div>
    <div className="text-center space-y-2 py-1 md:pb-3">
      {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
      <Typography level="h1">Set up your account</Typography>
      {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
      <div className="text-gray-600 font-semibold md:text-sm">
        Already have an account?{' '}
        {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
        <a href="/login" className="text-blue-600 hover:underline">
          Login
        </a>
      </div>
    </div>
  </>
);

export const ActivateForm = ({
  emailFromUrl,
  token,
}: {
  emailFromUrl: string;
  token: string;
}) => {
  const activateUserMutation = useActivateUser();

  const showError = activateUserMutation.isPending
    ? false
    : activateUserMutation.isError;

  const handleDismissError = () => {
    activateUserMutation.reset();
  };

  // Reset dismissed state and error when starting a new submission

  const form = useForm<ActivateFormData, typeof zodValidator>({
    defaultValues: {
      email: emailFromUrl,
      password: '',
      confirmPassword: '',
      role: '',
      department: '',
    },
    onSubmit: async ({ value }) => {
      if (activateUserMutation.isError) {
        activateUserMutation.reset();
      }
      activateUserMutation.mutate({
        email: value.email,
        password: value.password,
        role: value.role,
        department: value.department,
        token,
      });
    },
    validatorAdapter: zodValidator,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-3 md:space-y-6"
    >
      {/* Email */}
      <form.Field
        name="email"
        children={(field) => (
          <TextField
            label="Your email"
            placeholder="Your email address"
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            errorMessage={field.state.meta.errors?.[0] || undefined}
            isInvalid={!!field.state.meta.errors?.length}
            isDisabled={!!emailFromUrl}
          />
        )}
      />

      {/* Password */}
      <form.Field
        name="password"
        children={(field) => (
          <PasswordField
            label="Set your password"
            placeholder="Enter password"
            value={field.state.value}
            onChange={(value) => {
              field.handleChange(value);
            }}
            onBlur={field.handleBlur}
            errorMessage={field.state.meta.errors?.[0] || undefined}
            isInvalid={!!field.state.meta.errors?.length}
            showValidationOnFocus={true}
          />
        )}
      />

      {/* Confirm Password */}
      <form.Subscribe
        selector={(state) => state.values.password}
        children={(password) => (
          <form.Field
            name="confirmPassword"
            children={(field) => (
              <PasswordField
                label="Confirm your password"
                placeholder="Confirm password"
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={field.state.meta.errors?.[0] || undefined}
                isInvalid={!!field.state.meta.errors?.length}
              />
            )}
            validators={{
              onChange: ({ value }) => {
                if (!value) return undefined;
                return value === password ? undefined : "Passwords don't match";
              },
            }}
          />
        )}
      />

      {/* Role */}
      <form.Field
        name="role"
        children={(field) => (
          <Select
            id="role-select"
            name="role"
            label="What's your role"
            placeholder="Select your role"
            selectedKey={field.state.value ?? ''}
            onSelectionChange={(key) => {
              field.handleChange(key ? String(key) : '');
            }}
            errorMessage={field.state.meta.errors?.[0] || undefined}
            isInvalid={!!field.state.meta.errors?.length}
          >
            {ROLE_OPTIONS.map((option) => (
              <SelectItem
                id={option.key}
                key={option.key}
                textValue={option.label}
              >
                {option.label}
              </SelectItem>
            ))}
          </Select>
        )}
      />

      {/* Department */}
      <form.Field
        name="department"
        children={(field) => (
          <Select
            label="What's your department"
            placeholder="Select your department"
            selectedKey={field.state.value}
            onSelectionChange={(key) => {
              field.handleChange(key ? String(key) : '');
            }}
            onBlur={field.handleBlur}
            errorMessage={field.state.meta.errors?.[0] || undefined}
            isInvalid={!!field.state.meta.errors?.length}
          >
            {DEPARTMENT_OPTIONS.map((option) => (
              <SelectItem id={option.key} key={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        )}
      />
      <form.Subscribe
        selector={(state) => [
          state.isDirty,
          state.isSubmitting,
          state.isValid,
          state.values.email,
          state.values.password,
          state.values.confirmPassword,
          state.values.role,
          state.values.department,
        ]}
        children={([
          isDirty,
          isSubmitting,
          isValid,
          email,
          password,
          confirmPassword,
          role,
          department,
        ]) => (
          <div className="md:pt-3">
            <Button
              type="submit"
              isDisabled={
                !role ||
                !department ||
                !isValid ||
                !email ||
                !password ||
                !confirmPassword ||
                !!isSubmitting ||
                activateUserMutation.isPending ||
                !isDirty
              }
              variant="primary"
              customStyles="w-full"
            >
              {activateUserMutation.isPending
                ? 'Activating...'
                : 'Activate My Account'}
            </Button>
          </div>
        )}
      />

      {/* Error Alert */}
      <div className="relative">
        {showError && (
          <ErrorAlert
            message="There was an error activating your account. Please try again later."
            onDismiss={handleDismissError}
          />
        )}
      </div>

      {/* Terms */}
      {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
      <div className="text-xs text-gray-600 text-center">
        By clicking "Activate My Account" I agree to the{' '}
        <a
          href="https://www.breezeai.com/legal/terms-of-use"
          className="text-blue-600 hover:underline"
          target="_blank"
          // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
        >
          Terms of Use
        </a>{' '}
        &{' '}
        <a
          href="https://www.breezeai.com/legal/privacy-policy"
          className="text-blue-600 hover:underline"
          target="_blank"
          // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
        >
          Privacy Policy
        </a>
      </div>
    </form>
  );
};

const MobileActivatePage = ({
  emailFromUrl,
  token,
  currentYear,
}: {
  emailFromUrl: string;
  token: string;
  currentYear: number;
}) => (
  <div
    className="absolute grid place-items-center self-start p-4 max-h-full min-h-full w-full flex-grow bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: `url(${vesselIllustrationSrc})` }}
  >
    <div className="relative w-full max-h-full max-w-md bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-xl flex flex-col gap-2">
      <ActivateHeader />
      <ActivateForm emailFromUrl={emailFromUrl} token={token} />
      <div className="text-center text-xs text-gray-500">
        {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
        © {currentYear} Breeze. All rights reserved.
      </div>
    </div>
  </div>
);

const DesktopActivatePage = ({
  emailFromUrl,
  token,
  currentYear,
}: {
  emailFromUrl: string;
  token: string;
  currentYear: number;
}) => (
  <div className="grid grid-cols-2 py-6 px-12 items-center bg-white w-full">
    {/* Left Panel - Form */}
    <div className="grid place-items-center px-8 py-10 gap-5">
      <ActivateHeader />
      <ActivateForm emailFromUrl={emailFromUrl} token={token} />
    </div>

    {/* Right Panel - Illustration */}
    <div className="">
      <div className="max-w-[602px] max-h-[718px]">
        <img
          src={vesselIllustrationSrc}
          alt="Ship illustration"
          className="w-full h-full rounded-2xl"
        />
      </div>
    </div>
    {/* Footer */}
    <div className="text-center text-sm text-gray-500">
      {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
      © {currentYear} Breeze. All rights reserved.
    </div>
  </div>
);

export const ActivatePage = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || '';
  const currentYear = new Date().getFullYear();
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });
  const navigate = useNavigate();

  const { data, error } = useGetUserEmailByToken(token);

  useEffect(() => {
    if (error) {
      navigate('/login');
    }
  }, [error, navigate]);

  return (
    <CargoProvider theme="breeze">
      {isMobile
        ? data?.email && (
            <MobileActivatePage
              emailFromUrl={data?.email}
              token={token}
              currentYear={currentYear}
            />
          )
        : data?.email && (
            <DesktopActivatePage
              emailFromUrl={data?.email}
              token={token}
              currentYear={currentYear}
            />
          )}
    </CargoProvider>
  );
};
